import { PromiseExtended, Table } from 'dexie';
import { BaseModelForPush } from './base-model';
import {
  AddStudentData,
  ClassProficiencyResultsData,
  IAssessmentResult,
  IAssessments,
  ICheckedStudentExam,
  ICheckPoint,
  ICheckPointResult,
  IGradeSubject,
  InsertStudentData,
  IStudentData,
  IUser,
  StudentDataTransformer,
  SyncChanges,
  SyncRecord,
  SyncRecordStatus,
  UpdateStudentData,
  VerificationStatus,
  IStudentDuplicate,
  IStudentSubjectAnswerAnalysis,
  examCheckerDB,
  SessionStatus,
  FlnCategory,
  ICheckedStudentExamV2,
} from '@taleemabad/db';

import { lessonPlanDB } from './lessonplan';
import { v4 as uuid4 } from 'uuid';
import {
  CategoryData,
  StudentCheckpointFormData,
  StudentDetails,
} from '@taleemabad/dsm/components';
import { studentDBMigration } from '../migrations/students.migration';

export interface ICheckPointRecord extends ICheckPoint, SyncRecord {}
export interface ICheckPointResultRecord
  extends ICheckPointResult, SyncRecord {}
export interface ICheckPointAssessmentsRecord
  extends IAssessments, SyncRecord {}
export interface IUserRecord extends IUser, SyncRecord {}
export interface IStudentRecord extends IStudentData, SyncRecord {}
export interface IAssessmentResultRecord
  extends IAssessmentResult, SyncRecord {}
export interface ICheckedStudentExamRecord
  extends ICheckedStudentExam, SyncRecord {}
export interface IStudentDuplicateRecord
  extends IStudentDuplicate, SyncRecord {}

export interface IStudentSubjectAnswerAnalysisRecord
  extends IStudentSubjectAnswerAnalysis, SyncRecord {}

export interface ICheckedStudentExamV2Record
  extends ICheckedStudentExamV2, SyncRecord {}

export class StudentDB extends BaseModelForPush {
  checkpoint!: Table<ICheckPointRecord, number>;
  checkpointResults!: Table<ICheckPointResultRecord, number>;
  checkpointAssessments!: Table<ICheckPointAssessmentsRecord, number>;
  studentsDataV2!: Table<IStudentRecord, number>;
  studentsAssessmentData!: Table<IAssessmentResultRecord, number>;
  usersDataV2!: Table<IUserRecord, number>;
  checkedStudentExam!: Table<ICheckedStudentExamRecord, number>;
  studentDuplicate!: Table<IStudentDuplicateRecord, number>;
  studentSubjectAnswerAnalysis!: Table<
    IStudentSubjectAnswerAnalysisRecord,
    string
  >;
  checkedStudentExamV2!: Table<ICheckedStudentExamV2Record, string>;

  constructor() {
    super('studentdb');

    studentDBMigration.forEach(({ version, schema, upgrade }) => {
      const dbVersion = this.version(version).stores(schema);
      if (upgrade) {
        dbVersion.upgrade(upgrade);
      }
    });

    this.setupTableHooks(this.tables);
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  async areAllStudentsSynced(): Promise<boolean> {
    const student = await this.studentsDataV2
      .filter((record) => record.isNewCreated === true)
      .first();
    return !student;
  }

  getAllExamsForSpecificClassAndCheckpoint(
    classId: number,
    checkpointId: number,
  ): PromiseExtended<ICheckedStudentExamRecord[]> {
    return this.checkedStudentExam
      .where({ schoolClass: classId, checkpoint: checkpointId })
      .and((exam) => exam.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  getAllExamsForSpecificClassAndGradeSubject(
    classId: number,
    gradeSubjectId: number,
  ): PromiseExtended<ICheckedStudentExamRecord[]> {
    return this.checkedStudentExam
      .filter(
        (exam) =>
          exam.schoolClass === classId && exam.gradeSubject === gradeSubjectId,
      )
      .toArray();
  }

  getAllExamsForSpecificClassAndGradeSubjectAndCheckpoint(
    classId: number,
    gradeSubjectId: number,
    checkpointId: number,
  ): PromiseExtended<ICheckedStudentExamRecord[]> {
    return this.checkedStudentExam
      .filter(
        (exam) =>
          exam.schoolClass === classId &&
          exam.gradeSubject === gradeSubjectId &&
          exam.checkpoint === checkpointId,
      )
      .toArray();
  }

  getSpecificCheckpoint(
    checkpointId: number,
  ): PromiseExtended<ICheckPointRecord | undefined> {
    return this.checkpoint.get(checkpointId);
  }

  async calculateClassProficiencyResults(
    classId: number,
    gradeSubjectId: number,
    checkpointId: number,
  ): Promise<ClassProficiencyResultsData> {
    const exams =
      await this.getAllExamsForSpecificClassAndGradeSubjectAndCheckpoint(
        classId,
        gradeSubjectId,
        checkpointId,
      );

    return await StudentDataTransformer.calculateClassProficiencyResultsForExams(
      exams,
    );
  }

  async calculateSkillLevelsByCompetency(
    classId: number,
    gradeSubjectId: number,
    checkpointId: number,
  ): Promise<{ categories: string[]; series: CategoryData[] }> {
    const exams =
      await this.getAllExamsForSpecificClassAndGradeSubjectAndCheckpoint(
        classId,
        gradeSubjectId,
        checkpointId,
      );
    const bloomLevelsData =
      StudentDataTransformer.groupExamsByQuestionTags(exams);
    return await StudentDataTransformer.calculateSkillStatistics(
      bloomLevelsData,
    );
  }

  async getAllExamSubjects(
    classId: number,
    checkpointId: number,
  ): Promise<IGradeSubject[]> {
    const exams = await this.getAllExamsForSpecificClassAndCheckpoint(
      classId,
      checkpointId,
    );
    const gradeSubjects: number[] = [];
    exams.forEach((exam) => {
      if (!gradeSubjects.includes(exam.gradeSubject)) {
        gradeSubjects.push(exam.gradeSubject);
      }
    });

    const subjects: IGradeSubject[] = [];
    for (const subjectId of gradeSubjects) {
      const subject = await lessonPlanDB.getSelectedGradeSubject(subjectId);
      subjects.push(subject[0]);
    }

    return subjects;
  }

  async updateStudentExamRecord(
    examId: number,
    formData: StudentCheckpointFormData,
  ): Promise<void> {
    await this.checkedStudentExam.update(examId, {
      totalCheckpointAcademicDays: formData.totalAcademicDays,
      daysPresent: formData.daysPresent,
      classBehaviour: formData.classBehaviour.value,
    });
  }

  async updateStudentExamRecordMarks(
    examId: number,
    questionId: number | string,
    newTeacherMarks: string,
  ): Promise<void> {
    const exam = await this.checkedStudentExam.get(examId);

    if (!exam) return;

    const updatedQuestionAnswers = exam.questionAnswers!.map(
      (questionAnswer) => {
        if (
          questionAnswer.id === questionId ||
          questionAnswer.type === questionId
        ) {
          return {
            ...questionAnswer,
            teacherMarks: Number(newTeacherMarks),
          };
        }
        return questionAnswer;
      },
    );

    await this.checkedStudentExam.update(examId, {
      questionAnswers: updatedQuestionAnswers,
    });
  }

  async getStudentDetailsByExamId(
    examId: number,
  ): Promise<StudentDetails | null> {
    const examRecord = await this.checkedStudentExam.get(examId);
    if (!examRecord) {
      return null;
    }

    const allExams = await this.getAllExamsForSpecificClassAndCheckpoint(
      examRecord.schoolClass,
      examRecord.checkpoint,
    );
    const totalAcademicDays = examRecord.totalCheckpointAcademicDays;
    const daysPresent = examRecord.daysPresent;
    const classBehaviour = examRecord.classBehaviour;

    const totalOverallObtainedMarks = Number(examRecord.obtainedMarks);
    const totalOverallMarks = Number(examRecord.totalMarks);
    const totalPercentage =
      (totalOverallObtainedMarks / totalOverallMarks) * 100;

    const transformedData = StudentDataTransformer.transformExamData(allExams);
    const studentPosition =
      transformedData.find((item) => item.examId === examId.toString())
        ?.position || 0;

    const studentSubject = await lessonPlanDB.getSelectedGradeSubject(
      examRecord.gradeSubject,
    );
    const subjectLabel = studentSubject[0]?.subjectLabel || '--';

    const subjectId = studentSubject[0]?.id ?? null;

    const subjectMarks = [
      {
        subjectId,
        subjectLabel,
        obtainedMarks: Number(examRecord.obtainedMarks),
        totalMarks: Number(examRecord.totalMarks),
      },
    ];

    return {
      name: examRecord.studentName || '--',
      position: studentPosition,
      totalPercentage,
      totalOverallObtainedMarks,
      totalOverallMarks,
      subjectMarks,
      totalAcademicDays,
      daysPresent,
      classBehaviour,
    };
  }

  private async getStudentsSyncData(): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await this.studentsDataV2
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Deleted, SyncRecordStatus.Updated])
      .filter((record) => record.isNewCreated !== true)
      .toArray();
    return records;
  }

  private async getUsersSyncData(): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await this.usersDataV2
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Deleted, SyncRecordStatus.Updated])
      .filter((record) => record.isNewCreated !== true)
      .toArray();
    return records;
  }

  async getNewCreatedStudents(): Promise<PromiseExtended<SyncRecord[]>> {
    const records = (await this.getAllStudents()).filter(
      (data) =>
        data.isNewCreated && data.syncStatus === SyncRecordStatus.Created,
    );
    return records;
  }

  async getNewCreatedUsers(): Promise<PromiseExtended<SyncRecord[]>> {
    const records = (await this.getAllUsers()).filter(
      (data) =>
        data.isNewCreated && data.syncStatus === SyncRecordStatus.Created,
    );
    return records;
  }
  async getNewCreatedCheckedStudentExam(): Promise<
    PromiseExtended<SyncRecord[]>
  > {
    const records = (await this.checkedStudentExamV2.toArray()).filter(
      (data) => data.syncStatus === SyncRecordStatus.CreatedButNotSynced,
    );
    records.map((record) => {
      record.syncStatus = SyncRecordStatus.Created;
    });
    return records;
  }

  async getUpdatedCheckedStudentExam(): Promise<PromiseExtended<SyncRecord[]>> {
    return (await this.checkedStudentExamV2.toArray()).filter(
      (data) => data.syncStatus === SyncRecordStatus.Updated,
    );
  }

  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const records = {
      user: [
        ...(await this.getUsersSyncData()),
        ...(await this.getNewCreatedUsers()),
      ],
      studentProfile: [
        ...(await this.getStudentsSyncData()),
        ...(await this.getNewCreatedStudents()),
      ],
      checkedStudentExam: [
        ...(await this.getNewCreatedCheckedStudentExam()),
        ...(await this.getUpdatedCheckedStudentExam()),
      ],
    };
    return records;
  }

  async removeNewOrDeletedRecords(): Promise<void> {
    /// Filter out students whose admission number does not exist in duplicates
    /// Fetch the list of duplicate students
    const duplicates = await this.getAllDuplicateStudent();

    ///  Fetch the list of all students
    const students = await this.getAllStudents();

    ///  Create a set of admission numbers from the duplicate students
    const duplicateAdmissionNumbers = new Set(
      duplicates.map((student) => student.admissionNumber),
    );

    ///  Filter the list of all students to exclude those whose
    ///  admission number is in the set of duplicate admission numbers
    ///  and whose sync status deleted or new created
    const studentsToDelete = students
      .filter(
        (student) =>
          !duplicateAdmissionNumbers.has(student.admissionNumber ?? null),
      )
      .filter(
        (student) =>
          student.isNewCreated === true ||
          student.syncStatus === SyncRecordStatus.Deleted,
      );

    /// Bulk Delete the students from the studentsDataV2 table
    await this.studentsDataV2.bulkDelete(
      studentsToDelete
        .map((student) => student.id)
        .filter((id) => id !== undefined) as [],
    );

    await this.usersDataV2
      .filter(
        (data) =>
          data.isNewCreated === true ||
          data.syncStatus === SyncRecordStatus.Deleted,
      )
      .delete();
  }

  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;
    const idsToMarkSynced = {
      studentsDataV2: syncChanges.studentsDataV2?.created?.map(
        (record: { id: number }) => record.id,
      ),
      usersDataV2: syncChanges.usersDataV2?.created?.map(
        (record: { id: number }) => record.id,
      ),
      checkedStudentExamV2: syncChanges.checkedStudentExam?.created?.map(
        (record: { uuid: string }) => record.uuid,
      ),
    };

    for (const [model, ids] of Object.entries(idsToMarkSynced)) {
      if (!ids || (Array.isArray(ids) && ids.length === 0)) {
        continue;
      }
      updates += await this._updateSyncData(
        // @ts-ignore
        this[model],
        ids,
        status,
      );
    }
    return 1;
  }

  private async insertStudent(data: InsertStudentData) {
    await studentDB.studentsDataV2.add({
      userId: data.userId,
      uuid: data.uuid,
      name: data.name,
      schoolClass: data.gradeId,
      verificationStatus: VerificationStatus.NotVerified,
      isNewCreated: true,
      admissionNumber: data.admissionNumber,
      username: data.username,
      status: data.status,
    });
  }

  async addNewStudent(data: AddStudentData) {
    try {
      const userCheck = (await this.getAllUsers()).find(
        (user) =>
          user.username !== null &&
          user.username === data.username &&
          user.syncStatus !== SyncRecordStatus.Deleted,
      );

      let userId;
      if (userCheck) {
        userId = userCheck.id;
      } else {
        await studentDB.usersDataV2.add({
          uuid: uuid4(),
          username: data.username ?? null,
          isNewCreated: true,
          name: data.parentName ?? null,
          isUsernameDummyPhone: data.isUsernameDummyPhone,
          additionalPhoneNumbers: data.additionalPhoneNumbers ?? [],
        });
        const newUser = (await this.getAllUsers()).find(
          (user) => user.username === data.username,
        );
        userId = newUser?.id;
      }

      await this.insertStudent({
        name: data.name,
        userId: userId,
        uuid: uuid4(),
        schoolClass: data.gradeId,
        verificationStatus: VerificationStatus.NotVerified,
        isNewCreated: true,
        username: data.username ?? null,
        gradeId: data.gradeId,
        admissionNumber: data.admissionNumber ?? null,
        status: data.status ?? null,
      });
    } catch (error) {
      console.error('Error adding new student', error);
    }
  }

  private async createOrUpdateUserData(
    studentData: UpdateStudentData,
    createNewUser?: boolean,
  ) {
    // create new user
    if (createNewUser) {
      await this.usersDataV2.add({
        uuid: uuid4(),
        username: studentData.user.username ?? null,
        isNewCreated: true,
        name: studentData.user.name ?? null,
        isUsernameDummyPhone: false,
        additionalPhoneNumbers: studentData.user.additionalPhoneNumbers ?? [],
        syncStatus: SyncRecordStatus.Created,
      });
      return;
    }
    // update user
    await this.usersDataV2.where({ id: studentData?.user.id }).modify({
      name: studentData.user.name,
      additionalPhoneNumbers: studentData.user.additionalPhoneNumbers,
      isUsernameDummyPhone: studentData.user.isDummyPhone,
      syncStatus: studentData.user.isNewCreated
        ? SyncRecordStatus.Created
        : SyncRecordStatus.Updated,
    });
  }
  async updateStudentData(
    studentData: UpdateStudentData,
    userData?: IUserRecord,
  ) {
    await this.studentsDataV2.where({ id: studentData.id }).modify({
      name: studentData.name,
      schoolClass: studentData.gradeSubject?.schoolClass,
      statusChange: studentData.statusChange,
      status: studentData.status,
      admissionNumber: studentData.admissionNumber,
      verificationStatus: VerificationStatus.NotVerified,
      syncStatus: studentData.isNewCreated
        ? SyncRecordStatus.Created
        : SyncRecordStatus.Updated,
      userId: userData?.id ?? studentData.user.id,
      username: userData?.username ?? studentData.user.username,
    });
  }

  async updateStudentUser(studentData: UpdateStudentData) {
    try {
      // First get the current user data to check if username has changed
      const currentUser = (await this.getAllUsers()).find(
        (user) =>
          user.id === studentData.user.id ||
          user.uuid === studentData.user.uuid,
      );
      const isUsernameChanged =
        currentUser && currentUser.username !== studentData.user.username;

      // Now get the user data to check if username exists for another user
      const createNewUser =
        isUsernameChanged &&
        !(await this.getAllUsers()).find(
          (user) => user.username === studentData.user.username,
        );
      await this.createOrUpdateUserData(studentData, createNewUser);
      const user = (await this.getAllUsers()).find(
        (user) => user.username === studentData.user.username,
      );
      await this.updateStudentData(studentData, user);
    } catch (error) {
      console.error('Error updating student data', error);
    }
  }

  async updateStudentAdmissionNo(
    studentID: number,
    studentUUID?: string,
    studentName?: string,
    admissionNo?: string,
  ) {
    try {
      if (studentUUID) {
        const student = await this.studentsDataV2
          .filter(
            (item) => item.uuid === studentUUID && item.name === studentName, /// A user's UUID can be associated with multiple students, so we need to match the name as well
          )
          .first();
        await this.studentsDataV2.where({ uuid: student?.uuid }).modify({
          admissionNumber: admissionNo,
        });
        // remove object from duplicate student table based on student UUID
        await this.studentDuplicate.where({ uuid: student?.uuid }).delete();
      } else {
        const student = await this.studentsDataV2.get(studentID);
        await this.studentsDataV2.where({ id: student?.id }).modify({
          admissionNumber: admissionNo,
        });
        // remove object from duplicate student table based on student ID
        await this.studentDuplicate.where({ id: studentID }).delete();
      }
    } catch (error) {
      console.error('Error updating student admission no.', error);
    }
  }

  async getSpecificCheckpointDetail(
    checkpointId: number,
  ): Promise<ICheckPointRecord> {
    const checkpoint = await this.checkpoint.get(checkpointId);
    if (!checkpoint) {
      throw new Error(`Checkpoint with ID ${checkpointId} not found`);
    }
    return checkpoint;
  }

  getCheckpointsByGrade(gradeId: number): PromiseExtended<ICheckPointRecord[]> {
    return this.checkpoint
      .filter(
        (item) =>
          item.syncStatus !== SyncRecordStatus.Deleted &&
          item.grades.includes(gradeId),
      )
      .sortBy('id');
  }

  getCheckPoints(id: number): PromiseExtended<ICheckPointRecord[]> {
    return this.checkpoint
      .filter(
        (item) =>
          item.id === id && item.syncStatus !== SyncRecordStatus.Deleted,
      )
      .sortBy('id');
  }

  getAllCheckPoints(): PromiseExtended<ICheckPointRecord[]> {
    return this.checkpoint.toArray();
  }

  getCheckPointResults(id: number): PromiseExtended<ICheckPointResultRecord[]> {
    return this.checkpointResults
      .filter(
        (result) =>
          result.checkpoint === id &&
          result.syncStatus !== SyncRecordStatus.Deleted,
      )
      .sortBy('id');
  }

  getStudentsBasedOnSchoolClass(id: number): PromiseExtended<IStudentRecord[]> {
    return this.studentsDataV2
      .filter(
        (item) =>
          item.schoolClass === id &&
          item.syncStatus !== SyncRecordStatus.Deleted,
      )
      .sortBy('id');
  }

  async getStudentsWithFLNResults(
    schoolClassId: number,
    gradeSubjectId: number,
    checkpointId = 1, // Default to baseline assessment
  ): Promise<
    Array<
      IStudentRecord & { category: FlnCategory | null; examId: string | null }
    >
  > {
    try {
      const classStudents =
        await this.getStudentsBasedOnSchoolClass(schoolClassId);

      // Get categorizations from the new dedicated table
      const existingCategorizations = await this.checkedStudentExamV2
        .filter(
          (record) =>
            record.schoolClass === schoolClassId &&
            record.checkpoint === checkpointId &&
            record.gradeSubject === gradeSubjectId,
        )
        .toArray();

      const studentsWithCategories =
        classStudents?.map((student) => {
          const existingCategorization = existingCategorizations?.find(
            (record) => record.studentProfile === student.uuid,
          );

          return {
            ...student,
            category: existingCategorization?.studentCategory || null,
            examId: existingCategorization?.uuid || null,
          };
        }) || [];

      return studentsWithCategories;
    } catch (error) {
      console.error('Error loading students with FLN results:', error);
      throw error;
    }
  }

  // Simple method to save student category (create or update)
  async saveStudentCategory(data: {
    studentProfile: string;
    studentName: string;
    schoolClass: number;
    checkpoint: number;
    gradeSubject: number;
    category: FlnCategory;
  }): Promise<void> {
    try {
      // Check if record already exists
      const existing = await this.checkedStudentExamV2
        .filter(
          (exam) =>
            exam.studentProfile === data.studentProfile &&
            exam.checkpoint === data.checkpoint &&
            exam.gradeSubject === data.gradeSubject,
        )
        .first();

      if (existing) {
        let newSyncStatus: SyncRecordStatus;

        if (existing.syncStatus === SyncRecordStatus.CreatedButNotSynced) {
          // Keep the same status if it was created locally but not synced yet
          newSyncStatus = SyncRecordStatus.CreatedButNotSynced;
        } else {
          // Only set to Updated if it was already synced
          newSyncStatus = SyncRecordStatus.Updated;
        }

        // Update existing record using UUID
        if (existing.uuid) {
          await this.checkedStudentExamV2.update(existing.uuid, {
            studentCategory: data.category,
            syncStatus: newSyncStatus,
          });
        } else {
          throw new Error('Existing record missing UUID');
        }
      } else {
        const tempId = Date.now() + Math.floor(Math.random() * 1000);
        const newRecord: ICheckedStudentExamV2Record = {
          uuid: uuid4(),
          id: tempId,
          studentProfile: data.studentProfile,
          studentName: data.studentName,
          gradeSubject: data.gradeSubject,
          totalMarks: null,
          obtainedMarks: null,
          schoolClass: data.schoolClass,
          checkpoint: data.checkpoint,
          studentCategory: data.category,
          remarks: null,
          questionAnswers: null,
          totalCheckpointAcademicDays: null,
          daysPresent: null,
          classBehaviour: null,
          syncStatus: SyncRecordStatus.CreatedButNotSynced,
        };

        await this.checkedStudentExamV2.put(newRecord);
        console.log(
          `Created new categorization record for student ${data.studentProfile}`,
        );
      }
    } catch (error) {
      console.error('Error saving student categorization:', error);
      throw error;
    }
  }

  // Simple method to save student marks (create or update)
  async saveStudentMarks(data: {
    studentProfile: string;
    studentName: string;
    schoolClass: number;
    checkpoint: number;
    gradeSubject: number;
    obtainedMarks: number;
    totalMarks: number;
    studentCategory?: FlnCategory | null;
  }): Promise<void> {
    try {
      // Check if record already exists
      const existing = await this.checkedStudentExamV2
        .filter(
          (exam) =>
            exam.studentProfile === data.studentProfile &&
            exam.checkpoint === data.checkpoint &&
            exam.gradeSubject === data.gradeSubject,
        )
        .first();

      if (existing) {
        let newSyncStatus: SyncRecordStatus;

        if (existing.syncStatus === SyncRecordStatus.CreatedButNotSynced) {
          // Keep the same status if it was created locally but not synced yet
          newSyncStatus = SyncRecordStatus.CreatedButNotSynced;
        } else {
          // Only set to Updated if it was already synced
          newSyncStatus = SyncRecordStatus.Updated;
        }

        // Update existing record using UUID
        if (existing.uuid) {
          await this.checkedStudentExamV2.update(existing.uuid, {
            obtainedMarks: data.obtainedMarks.toString(),
            totalMarks: data.totalMarks.toString(),
            studentCategory: data.studentCategory || existing.studentCategory,
            syncStatus: newSyncStatus,
          });
          console.log(
            `Updated marks record for student ${data.studentProfile}`,
          );
        } else {
          throw new Error('Existing record missing UUID');
        }
      } else {
        const tempId = Date.now() + Math.floor(Math.random() * 1000);
        const newRecord: ICheckedStudentExamV2Record = {
          uuid: uuid4(),
          id: tempId,
          studentProfile: data.studentProfile,
          studentName: data.studentName,
          gradeSubject: data.gradeSubject,
          totalMarks: data.totalMarks.toString(),
          obtainedMarks: data.obtainedMarks.toString(),
          schoolClass: data.schoolClass,
          checkpoint: data.checkpoint,
          studentCategory: data.studentCategory || null,
          remarks: null,
          questionAnswers: null,
          totalCheckpointAcademicDays: null,
          daysPresent: null,
          classBehaviour: null,
          syncStatus: SyncRecordStatus.CreatedButNotSynced,
        };

        await this.checkedStudentExamV2.put(newRecord);
        console.log(
          `Created new marks record for student ${data.studentProfile}`,
        );
      }
    } catch (error) {
      console.error('Error saving student marks:', error);
      throw error;
    }
  }

  // Method to handle complete manual results loading and transformation
  async getManualResultsData(
    schoolClassId: number,
    gradeSubjectId: number,
    checkpointId: number,
    totalMarks = 100,
  ): Promise<{
    students: Array<
      IStudentRecord & {
        obtainedMarks: number | null;
        totalMarks: number;
        studentCategory: FlnCategory | null;
        category: FlnCategory | null;
        examId: string | null;
      }
    >;
  }> {
    try {
      // Get students with their FLN results
      const studentsWithFLNResults = await this.getStudentsWithFLNResults(
        schoolClassId,
        gradeSubjectId,
        checkpointId,
      );

      // Transform and enrich each student with marks data
      const studentsWithManualResults = await Promise.all(
        studentsWithFLNResults.map(async (student) => {
          // Map FLN category to manual result category
          let studentCategory: FlnCategory | null = null;
          if (student.category) {
            studentCategory = student.category;
          }

          // Get obtained marks from the exam record if it exists
          let obtainedMarks: number | null = null;
          let totalMarksNum = totalMarks;

          if (student.examId) {
            try {
              const examRecord = await this.checkedStudentExamV2.get(
                student.examId,
              );
              if (examRecord) {
                obtainedMarks = examRecord.obtainedMarks
                  ? parseInt(examRecord.obtainedMarks)
                  : null;
                totalMarksNum = examRecord.totalMarks
                  ? parseInt(examRecord.totalMarks)
                  : totalMarks;
              }
            } catch (error) {
              console.error(
                'Error fetching exam record for student:',
                student.uuid,
                error,
              );
            }
          }

          return {
            ...student,
            obtainedMarks,
            totalMarks: totalMarksNum,
            studentCategory,
            // examId and category are already included from getStudentsWithFLNResults
          };
        }),
      );

      return {
        students: studentsWithManualResults,
      };
    } catch (error) {
      console.error('Error loading manual results data:', error);
      throw error;
    }
  }

  // Method to save manual results for multiple students
  async saveManualResults(
    students: Array<{
      uuid: string;
      name: string;
      obtainedMarks?: number | null;
      totalMarks?: number;
      studentCategory?: FlnCategory | null;
    }>,
    schoolClassId: number,
    gradeSubjectId: number,
    checkpointId: number,
  ): Promise<void> {
    try {
      for (const student of students) {
        if (!student.uuid) {
          console.error('Student UUID not found:', student);
          continue;
        }

        // Check if this student has any changes to save
        const hasMarks =
          student.obtainedMarks !== null && student.obtainedMarks !== undefined;
        const hasCategory =
          student.studentCategory !== null &&
          student.studentCategory !== undefined;

        if (!hasMarks && !hasCategory) {
          continue; // Skip students with no data to save
        }

        // First save the category if it exists
        if (hasCategory) {
          await this.saveStudentCategory({
            studentProfile: student.uuid,
            studentName: student.name || '',
            schoolClass: schoolClassId,
            checkpoint: checkpointId,
            gradeSubject: gradeSubjectId,
            category: student.studentCategory as FlnCategory,
          });
        }

        // Now update the record with marks if they exist
        if (hasMarks) {
          await this.saveStudentMarks({
            studentProfile: student.uuid,
            studentName: student.name || '',
            schoolClass: schoolClassId,
            checkpoint: checkpointId,
            gradeSubject: gradeSubjectId,
            obtainedMarks: student.obtainedMarks || 0,
            totalMarks: student.totalMarks || 100,
            studentCategory: student.studentCategory,
          });
        }

        console.log(
          `Saved results for student ${student.name}: marks=${student.obtainedMarks}/${student.totalMarks}, category=${student.studentCategory}`,
        );
      }
    } catch (error) {
      console.error('Error saving manual results:', error);
      throw error;
    }
  }

  getAllStudents(): PromiseExtended<IStudentRecord[]> {
    return this.studentsDataV2.toArray();
  }

  getStudentsDataByUUID(uuid: string): PromiseExtended<IStudentRecord[]> {
    return this.studentsDataV2
      .filter((item) => item.uuid === uuid)
      .sortBy('id');
  }

  getAllUsers(): PromiseExtended<IUserRecord[]> {
    return this.usersDataV2.toArray();
  }

  getStudentsAssessmentData(
    id: number,
  ): PromiseExtended<IAssessmentResultRecord[]> {
    return this.studentsAssessmentData
      .filter((item) => item.student === id)
      .toArray();
  }

  async getStudentInfo(studentId: number) {
    const studentUser = await this.studentsDataV2.get(studentId);
    return this.retrieveStudentDetails(studentUser);
  }

  async getStudentInfoBasedOnUUID(studentId: string) {
    const studentUser = await this.studentsDataV2
      .filter((item) => item.uuid === studentId)
      .first();
    return this.retrieveStudentDetails(studentUser);
  }

  private async retrieveStudentDetails(studentUser: any) {
    const user = await this.usersDataV2.get(studentUser?.userId ?? 0);
    const schoolClass = (await lessonPlanDB.getSchoolClasses()).find(
      (classItem) => classItem.id === studentUser?.schoolClass,
    );
    const gradeSubject = (await lessonPlanDB.getGradeSubjects()).find(
      (item) => item.grade === schoolClass?.grade,
    );
    const grade = (await lessonPlanDB.getGrades()).find(
      (item) => item.id === gradeSubject?.grade,
    );

    return {
      ...studentUser,
      user,
      schoolClass,
      grade,
      gradeSubject,
    };
  }

  async getStudentsWithAssignedClasses() {
    const assignedClasses = await lessonPlanDB.getTimetableEntries();
    const students = await this.getAllStudents();

    return assignedClasses.map((assignedClass) => {
      const studentsInClass = students.filter(
        (student) =>
          student.schoolClass === assignedClass?.schoolClass &&
          student.syncStatus !== SyncRecordStatus.Deleted,
      );

      return {
        ...assignedClass,
        students: studentsInClass,
      };
    });
  }

  async markedVerified(studentId: number) {
    await this.studentsDataV2.update(studentId, {
      verificationStatus: VerificationStatus.Verified,
    });
  }

  async updateStudentStatus(id: number, status: SessionStatus) {
    await this.studentsDataV2.where({ id: id }).modify({
      status: status,
      statusChange: true,
    });
  }

  async removeStudentData(studentId: number) {
    const student = await this.studentsDataV2.get(studentId);
    if (student?.isNewCreated) {
      await this.studentsDataV2.delete(studentId);
    } else {
      await this.studentsDataV2.update(studentId, {
        syncStatus: SyncRecordStatus.Deleted,
      });
    }
    if (student?.admissionNumber) {
      /// delete Duplicate objects based on Admission Number
      await this.studentDuplicate
        .filter((item) => item.admissionNumber === student.admissionNumber)
        .delete();
    }

    // we also need to delete student pictures if any
    if (student?.uuid) {
      examCheckerDB.deleteExamFileByStudentUuid(student.uuid);
    }
  }

  async getUserByPhone(value: string) {
    return studentDB.usersDataV2
      .filter((user) => user.username === value)
      .first();
  }

  checkIfUsernameExists = async (value: string) => {
    return (
      (await this.getAllUsers()).filter((user) => user.username === value)
        ?.length > 0
    );
  };

  checkIfAdmissionNoExists = async (value: string) => {
    if (value === null || value === '') {
      return false;
    }
    const studentExistInStudents = (await this.getAllStudents()).find(
      (item) =>
        item.admissionNumber === value &&
        item.syncStatus !== SyncRecordStatus.Deleted,
    );
    const studentExistInDuplicates = (await this.getAllDuplicateStudent()).find(
      (item) => item.admissionNumber === value,
    );
    return studentExistInStudents || studentExistInDuplicates;
  };

  async addBulkDuplicateStudents(data: IStudentDuplicate[]) {
    const filteredData = data.filter(
      (item) =>
        item.existingObject !== undefined && item.existingObject !== null,
    );
    await this.studentDuplicate.bulkPut(filteredData);
  }

  getAllDuplicateStudent(): PromiseExtended<IStudentDuplicate[]> {
    return this.studentDuplicate.toArray();
  }

  async getDuplicateStudentBasedOnSchoolClass(schoolClass: number) {
    const allDuplicateStudents = await this.getAllDuplicateStudent();
    // Filter students based on school class
    const filteredStudents = allDuplicateStudents.filter(
      (item) => item.schoolClass === schoolClass,
    );
    // Map students to the formatted output with grade retrieval
    const studentsWithGrade = await Promise.all(
      filteredStudents.map(async (item) => {
        // Get grade for the current student schoolClass
        const currentGrade = await lessonPlanDB.getSpecificGradeSubjectClass(
          item.schoolClass ?? 0,
        );
        const currentGradeSubjectClass = `${currentGrade?.gradeLabel || ''} - ${currentGrade?.section || ''}`;
        // Get grade for the existing object schoolClass, if it exists
        const existingGrade = item.existingObject
          ? await lessonPlanDB.getSpecificGradeSubjectClass(
              item.existingObject.schoolClass ?? 0,
            )
          : null;
        const existingGradeSubjectClass = `${existingGrade?.gradeLabel || ''} - ${existingGrade?.section || ''}`;
        // Create the formatted existing and new object structure
        const formattedExisting = item.existingObject
          ? {
              ...item.existingObject,
              gradeSubject: existingGradeSubjectClass,
            }
          : null;
        const formatted = {
          ...item,
          existingObject: formattedExisting,
          gradeSubject: currentGradeSubjectClass,
        };

        return formatted;
      }),
    );
    return studentsWithGrade;
  }

  async updateStudentWithInviteStatus(data: IStudentRecord[]) {
    data.forEach(async (student) => {
      await this.studentsDataV2.where({ id: student.id }).modify({
        inviteStatus: true,
      });
    });
  }
}

export const studentDB = new StudentDB();
